import { ref, type Ref } from 'vue'
import { reviewTemplates } from '../../integrations/personalReview'
import { TEMPLATE_STORAGE_KEY } from '../model/constants'
import { coerceFlowGraphSnapshot, toTemplateGraph } from '../model/graphCodec'
import type { FlowGraphV1, FlowSnapshot, FlowTemplate, InspectorPanel } from '../model/types'
import { clone, type FlowDocument } from './useFlowDocument'
import type { FlowHistory } from './useFlowHistory'
import type { FlowSelection } from './useFlowSelection'
import type { FlowViewport } from './useFlowViewport'

type Deps = {
  document: FlowDocument
  selection: FlowSelection
  history: FlowHistory
  viewport: FlowViewport
  inspectorPanel: Ref<InspectorPanel>
  /** The graph as saved; whole-canvas templates store exactly this. */
  currentGraph: () => FlowGraphV1
  /** Replaces the canvas with a loaded graph. */
  load: (snap: FlowSnapshot) => void
}

const STATUS_DURATION_MS = 2500

/** Reusable flow snippets stored in this browser, shared by all decks. */
export function useFlowTemplates(deps: Deps) {
  const { document, selection, history } = deps
  const templateList = ref<FlowTemplate[]>([])
  const templateName = ref('')
  const templateStatus = ref<string | null>(null)
  const renamingTemplateId = ref<string | null>(null)
  const renamingTemplateName = ref('')

  const loadTemplates = () => {
    try {
      const raw = localStorage.getItem(TEMPLATE_STORAGE_KEY)
      templateList.value = reviewTemplates(raw ? (JSON.parse(raw) as FlowTemplate[]) : [])
    } catch {
      templateList.value = []
    }
  }

  const store = (list: FlowTemplate[]) => {
    try {
      localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(list))
    } catch {
      templateStatus.value = 'Storage error — could not save.'
    }
    templateList.value = list
  }

  const flashStatus = (message: string) => {
    templateStatus.value = message
    setTimeout(() => {
      templateStatus.value = null
    }, STATUS_DURATION_MS)
  }

  const findTemplate = (id: string) => templateList.value.find((t) => t.id === id)

  /** Saves the whole canvas under the typed name. */
  const saveTemplate = () => {
    const name = templateName.value.trim()
    if (!name) {
      templateStatus.value = 'Enter a template name.'
      return
    }
    const graph = JSON.parse(JSON.stringify(deps.currentGraph())) as FlowGraphV1
    store([...templateList.value, { id: `tpl-${Date.now()}`, name, savedAt: Date.now(), graph }])
    templateName.value = ''
    flashStatus(`Saved "${name}".`)
  }

  /** Saves the selected nodes and the edges between them under a prompted name. */
  const saveSelectionAsTemplate = () => {
    const selectedIds = new Set(selection.selectedNodeIds.value)
    if (selectedIds.size === 0) return
    const name = window.prompt('Template name:')
    if (!name?.trim()) return
    const graph = toTemplateGraph(
      document.nodes.value.filter((n) => selectedIds.has(n.id)),
      document.edges.value.filter(
        (e) => selectedIds.has(e.fromNodeId) && selectedIds.has(e.toNodeId),
      ),
    )
    const template = { id: `tpl-${Date.now()}`, name: name.trim(), savedAt: Date.now(), graph }
    store([...templateList.value, template])
    flashStatus(`Saved "${template.name}".`)
    deps.inspectorPanel.value = 'templates'
  }

  /** Replaces the canvas with a template after confirmation. */
  const applyTemplateById = (id: string) => {
    const template = findTemplate(id)
    if (!template) return
    if (!confirm(`Load "${template.name}"? This will replace the current canvas.`)) return
    const snap = coerceFlowGraphSnapshot(template.graph)
    if (!snap) {
      templateStatus.value = 'Template is corrupt.'
      return
    }
    history.runMutation('Load template', () => deps.load(snap))
    flashStatus(`Loaded "${template.name}".`)
  }

  /** Adds a template's nodes centred in the view, with fresh ids, keeping the canvas. */
  const addTemplateById = (id: string) => {
    const template = findTemplate(id)
    if (!template) return
    const snap = coerceFlowGraphSnapshot(template.graph)
    if (!snap) {
      templateStatus.value = 'Template is corrupt.'
      return
    }
    history.runMutation('Add template', () => {
      const idMap = new Map<string, string>()
      for (const n of snap.nodes) idMap.set(n.id, document.nextNodeId())
      const center = deps.viewport.viewportCenter()
      const xs = snap.nodes.map((n) => n.x)
      const ys = snap.nodes.map((n) => n.y)
      const dx = center.x - (Math.min(...xs) + Math.max(...xs)) / 2
      const dy = center.y - (Math.min(...ys) + Math.max(...ys)) / 2
      const addedIds: string[] = []
      for (const n of snap.nodes) {
        const newId = idMap.get(n.id)!
        document.nodes.value.push({
          ...clone(n),
          id: newId,
          locked: false,
          x: Math.round(n.x + dx),
          y: Math.round(n.y + dy),
        })
        addedIds.push(newId)
      }
      for (const e of snap.edges) {
        const from = idMap.get(e.fromNodeId)
        const to = idMap.get(e.toNodeId)
        if (!from || !to) continue
        document.edges.value.push({
          ...clone(e),
          id: document.nextEdgeId(),
          fromNodeId: from,
          toNodeId: to,
        })
      }
      selection.selectNodes(addedIds)
    })
  }

  const deleteTemplateById = (id: string) => {
    const template = findTemplate(id)
    if (!template || !confirm(`Delete "${template.name}"?`)) return
    store(templateList.value.filter((t) => t.id !== id))
  }

  const startRenameTemplate = (template: FlowTemplate) => {
    renamingTemplateId.value = template.id
    renamingTemplateName.value = template.name
  }

  const commitRenameTemplate = () => {
    const id = renamingTemplateId.value
    const newName = renamingTemplateName.value.trim()
    if (id && newName) {
      store(templateList.value.map((t) => (t.id === id ? { ...t, name: newName } : t)))
    }
    renamingTemplateId.value = null
  }

  loadTemplates()

  return {
    templateList,
    templateName,
    templateStatus,
    renamingTemplateId,
    renamingTemplateName,
    saveTemplate,
    saveSelectionAsTemplate,
    applyTemplateById,
    addTemplateById,
    deleteTemplateById,
    startRenameTemplate,
    commitRenameTemplate,
  }
}

export type FlowTemplates = ReturnType<typeof useFlowTemplates>
